import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, timeout: 30000 });

// Global 402 interceptor: triggers the quota modal and rejects so calling code knows it failed.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 402 && err?.response?.data?.detail && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("goldora:quota-blocked", { detail: err.response.data.detail }));
    }
    return Promise.reject(err);
  }
);

// Pull stored user email (if any) to attach to gated endpoints
const getUserEmail = () => {
  try {
    const raw = localStorage.getItem("intelvest_user");
    return raw ? (JSON.parse(raw).email || null) : null;
  } catch { return null; }
};

export const withEmail = (payload = {}) => ({ ...payload, email: payload.email ?? getUserEmail() });

export const getCurrencies = () => api.get("/currencies").then((r) => r.data);
export const getCurrencyHistory = (base, target, days = 30) =>
  api.get(`/currencies/history?base=${base}&target=${target}&days=${days}`).then((r) => r.data);
export const getCrypto = () => api.get("/crypto").then((r) => r.data);
export const getCryptoHistory = (id, days = 30) =>
  api.get(`/crypto/history?id=${id}&days=${days}`).then((r) => r.data);
export const getIndices = () => api.get("/markets/indices").then((r) => r.data);
export const getCountries = () => api.get("/countries").then((r) => r.data);
export const getCountry = (code) => api.get(`/countries/${code}`).then((r) => r.data);
export const getAssetClasses = () => api.get("/asset-classes").then((r) => r.data);
export const postComparator = (payload) => api.post("/comparator", withEmail(payload)).then((r) => r.data);
export const postSimulator = (payload) => api.post("/simulator", withEmail(payload)).then((r) => r.data);
export const postChat = (payload) => api.post("/ai/chat", withEmail(payload)).then((r) => r.data);
export const getChatHistory = (sessionId) => api.get(`/ai/history/${sessionId}`).then((r) => r.data);
export const getUsage = () => {
  const email = getUserEmail();
  return api.get(`/usage/me${email ? `?email=${encodeURIComponent(email)}` : ""}`).then((r) => r.data);
};
export const getWeeklyInsight = (lang = "pt") =>
  api.get(`/insights/weekly?lang=${encodeURIComponent(lang)}`, { timeout: 60000 }).then((r) => r.data);
export const postCoachScore = (payload) =>
  api.post("/coach/score", withEmail(payload)).then((r) => r.data);
export const postCoachChat = (payload) =>
  api.post("/coach/chat", withEmail(payload), { timeout: 60000 }).then((r) => r.data);
export const getCoachStreak = (email, lang = "pt") =>
  api.get(`/coach/streak/${encodeURIComponent(email)}?lang=${encodeURIComponent(lang)}`).then((r) => r.data);
export const postCoachCheckin = (payload) =>
  api.post("/coach/checkin", withEmail(payload)).then((r) => r.data);

// ---- Auth ----
const TOKEN_KEY = "goldora_session_token";
export const getSessionToken = () => { try { return localStorage.getItem(TOKEN_KEY); } catch { return null; } };
export const setSessionToken = (t) => { try { localStorage.setItem(TOKEN_KEY, t); } catch {/* */} };
export const clearSessionToken = () => { try { localStorage.removeItem(TOKEN_KEY); } catch {/* */} };

api.interceptors.request.use((cfg) => {
  const tok = getSessionToken();
  if (tok) cfg.headers.Authorization = `Bearer ${tok}`;
  return cfg;
});

export const setPin = (email, pin) => api.post("/auth/set-pin", { email, pin }).then((r) => r.data);
export const loginPin = (email, pin) => api.post("/auth/login", { email, pin }).then((r) => r.data);
export const logout = () => api.post("/auth/logout").then((r) => r.data).catch(() => ({}));
export const fetchMe = () => api.get("/auth/me").then((r) => r.data);
export const requestPinReset = (email) => api.post("/auth/reset-pin/request", { email }).then((r) => r.data);
export const confirmPinReset = (token, new_pin) => api.post("/auth/reset-pin/confirm", { token, new_pin }).then((r) => r.data);
export const googleSession = (session_id) => api.post("/auth/google/session", { session_id }).then((r) => r.data);
export const getWelcomeBack = (email, lang = "pt") =>
  api.get(`/users/welcome-back?email=${encodeURIComponent(email)}&lang=${encodeURIComponent(lang)}`).then((r) => r.data);
export const getAdminStats = () => api.get("/admin/stats").then((r) => r.data);
export const getAdminFunnel = (days = 30) => api.get(`/admin/funnel?days=${days}`).then((r) => r.data);
export const getAdminMe = () => api.get("/admin/me").then((r) => r.data);
